import React, { useEffect, useState } from "react";
import { View, ScrollView } from "react-native";
import SettingsCard from "../../components/feature/settings/common/settings-card";
import StyledText from "../../components/common/ui/styled-text";
import { Input } from "../../components/common/ui/input.native";
import { ActionButton } from "../../components/ui/action-button";
import { Upload, AlertCircle } from "lucide-react-native";
import { useBrandingData, useUploadBranding } from "../../hooks/useBranding";
import { useToast } from "../../contexts/ToastContext";
import LoadingView from "../../components/common/loading-view";
import { ERROR_RED, GRAY_50 } from "../../constants/colors";

const Branding = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [brandColor, setBrandColor] = useState("#000000");
  const [accentColor, setAccentColor] = useState("#000000");
  const { data: brandingData, isLoading } = useBrandingData();
  const { mutateAsync: uploadBranding, isPending: isUploading } =
    useUploadBranding();
  const { showSuccessToast } = useToast();

  useEffect(() => {
    if (brandingData) {
      if (brandingData.brandColor) {
        setBrandColor(brandingData.brandColor);
      }
      if (brandingData.accentColor) {
        setAccentColor(brandingData.accentColor);
      }
    }
  }, [brandingData]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const maxSize = 512 * 1024; // 512KB
      if (file.size > maxSize) {
        setFileError(
          `File size must be less than 512KB. Current file size: ${(file.size / 1024).toFixed(1)}KB`
        );
        setSelectedFile(null);
        event.target.value = "";
        return;
      }
      setFileError(null);
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    setError(null);
    try {
      await uploadBranding({
        ...(selectedFile && { logo: selectedFile }),
        ...(brandColor && { brandColor }),
        ...(accentColor && { accentColor }),
      });
      showSuccessToast("Branding updated successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Upload failed. Please try again."
      );
    }
  };

  if (isLoading) {
    return <LoadingView backgroundColor={GRAY_50} />;
  }

  return (
    <ScrollView className="flex-1 bg-gray-50 pb-16">
      <View className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6">
        <SettingsCard title="Business Logo">
          <View className="space-y-4">
            {brandingData?.logo && (
              <View className="space-y-3">
                <StyledText className="text-sm font-medium text-gray-700">
                  Current Logo
                </StyledText>
                <View className="flex items-center justify-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <img
                    src={brandingData.logo}
                    alt="Current business logo"
                    className="max-w-full max-h-32 object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                    }}
                  />
                </View>
              </View>
            )}

            <View className="space-y-2">
              <StyledText className="text-sm font-medium text-gray-700">
                Upload your business logo
              </StyledText>
              <StyledText className="text-xs text-gray-500">
                Supported formats: JPEG, PNG, GIF. Maximum size: 512KB
              </StyledText>
            </View>

            <View className="space-y-3">
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif"
                onChange={handleFileChange}
                disabled={isUploading}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />

              {selectedFile && (
                <View className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <View className="flex items-center justify-between">
                    <StyledText className="text-sm font-medium text-gray-900">
                      {selectedFile.name}
                    </StyledText>
                    <StyledText className="text-xs text-gray-500">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </StyledText>
                  </View>
                  <StyledText className="text-xs text-gray-500 text-center">
                    Type: {selectedFile.type}
                  </StyledText>
                </View>
              )}

              {fileError && (
                <View className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <View className="flex items-center space-x-2">
                    <AlertCircle size={16} color={ERROR_RED} />
                    <StyledText className="text-sm text-red-800">
                      {fileError}
                    </StyledText>
                  </View>
                </View>
              )}
            </View>
          </View>
        </SettingsCard>

        <SettingsCard title="Brand Colors">
          <View className="space-y-6">
            <StyledText className="text-sm text-gray-600">
              Choose colors that represent your brand identity
            </StyledText>

            <View className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <View className="space-y-3">
                <StyledText className="text-sm font-medium text-gray-700">
                  Primary Brand Color
                </StyledText>
                <View className="flex items-center space-x-3">
                  <input
                    type="color"
                    id="brandColor"
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="w-12 h-12 rounded-lg border border-gray-300 cursor-pointer"
                  />
                  <View className="flex-1">
                    <Input
                      value={brandColor}
                      onChangeText={setBrandColor}
                      placeholder="#000000"
                      className="font-mono text-sm"
                    />
                  </View>
                </View>
              </View>

              <View className="space-y-3">
                <StyledText className="text-sm font-medium text-gray-700">
                  Accent Color
                </StyledText>
                <View className="flex items-center space-x-3">
                  <input
                    type="color"
                    id="accentColor"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    className="w-12 h-12 rounded-lg border border-gray-300 cursor-pointer"
                  />
                  <View className="flex-1">
                    <Input
                      value={accentColor}
                      onChangeText={setAccentColor}
                      placeholder="#000000"
                      className="font-mono text-sm"
                    />
                  </View>
                </View>
              </View>
            </View>
          </View>
        </SettingsCard>

        <View className="flex justify-end">
          <ActionButton
            label={isUploading ? "Uploading..." : "Save Branding"}
            icon={Upload}
            variant="primary"
            onPress={handleUpload}
            disabled={
              (!selectedFile && !brandColor && !accentColor) || isUploading
            }
            isLoading={isUploading}
            loadingLabel="Uploading..."
          />
        </View>

        {error && (
          <View className="bg-red-50 border border-red-200 rounded-lg p-4">
            <View className="flex items-center space-x-2">
              <AlertCircle size={20} color={ERROR_RED} />
              <StyledText className="text-sm text-red-800">{error}</StyledText>
            </View>
          </View>
        )}
      </View>
    </ScrollView>
  );
};

export default Branding;
